/**
 * AlertEngineService – evaluates configurable rules against a UIContext to
 * produce active alerts. Rules can come from the backend (via API) or from
 * a local default set. Storage of resolved/dismissed alerts is in-memory
 * for now; a future backend (AlertRules / AlertEvents tables) can replace
 * the loader without changing consumers.
 *
 * Rule format (matches the spec in copilot-instructions and the backend
 * AlertRules table):
 *
 *   {
 *     id, programId?, name, severity,
 *     actionType: 'NOTIFY' | 'CREATE_TASK' | 'ESCALATE',
 *     targetRole?, isActive,
 *     condition: { field, operator, value }   // or { all: [...] } / { any: [...] }
 *   }
 *
 * Field paths use dot notation against the UIContext, plus virtual fields
 * resolved by `resolveField()` (e.g. "kpi.pendingApprovals",
 * "entity.approvedAmount", "workflow.pendingTasks").
 */

import type { UIContext, UIKpi } from './UIContextExtractor';
import { findKpi } from './UIContextExtractor';

export type AlertSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';

export type AlertActionType = 'NOTIFY' | 'CREATE_TASK' | 'ESCALATE';

export type AlertOperator =
  | '=='
  | '!='
  | '>'
  | '>='
  | '<'
  | '<='
  | 'contains'
  | 'startsWith'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'in';

export interface AlertCondition {
  field?: string;
  operator?: AlertOperator;
  value?: unknown;
  /** Logical AND of nested conditions. */
  all?: AlertCondition[];
  /** Logical OR of nested conditions. */
  any?: AlertCondition[];
}

export interface AlertRule {
  id: string;
  programId?: string;
  name: string;
  message: string;
  severity: AlertSeverity;
  actionType: AlertActionType;
  /** Restrict to users with this role (substring match, case-insensitive). */
  targetRole?: string;
  /** Restrict to specific page types. Empty = all. */
  pageTypes?: string[];
  isActive: boolean;
  condition: AlertCondition;
  /** Optional CSS selector to focus when the user clicks the alert. */
  target?: string;
}

export interface AlertEvent {
  id: string;
  ruleId: string;
  programId?: string;
  entityId?: string;
  entityType?: string;
  message: string;
  severity: AlertSeverity;
  actionType: AlertActionType;
  status: 'ACTIVE' | 'RESOLVED';
  createdAt: string;
  target?: string;
}

// ──────────────────────────────────────────────────────────────────────
// Field resolver
// ──────────────────────────────────────────────────────────────────────

function resolveField(ctx: UIContext, path: string): unknown {
  if (!path) return undefined;

  // Virtual KPI accessor: kpi.<key-or-label-substring>
  if (path.startsWith('kpi.')) {
    const needle = path.slice(4);
    const kpi: UIKpi | undefined = findKpi(ctx, needle);
    if (!kpi) return undefined;
    if (typeof kpi.value === 'number') return kpi.value;
    const numeric = Number(String(kpi.value).replace(/[^\d.-]/g, ''));
    return Number.isFinite(numeric) ? numeric : kpi.value;
  }

  // Generic dot-path against UIContext (entity, workflow, tableSummary, ...).
  const segments = path.split('.');
  let cur: unknown = ctx;
  for (const seg of segments) {
    if (cur === null || cur === undefined) return undefined;
    if (typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

// ──────────────────────────────────────────────────────────────────────
// Condition evaluator
// ──────────────────────────────────────────────────────────────────────

function compare(left: unknown, op: AlertOperator, right: unknown): boolean {
  switch (op) {
    case '==':
      // eslint-disable-next-line eqeqeq
      return left == right;
    case '!=':
      // eslint-disable-next-line eqeqeq
      return left != right;
    case '>':
      return Number(left) > Number(right);
    case '>=':
      return Number(left) >= Number(right);
    case '<':
      return Number(left) < Number(right);
    case '<=':
      return Number(left) <= Number(right);
    case 'contains':
      return String(left ?? '').toLowerCase().includes(String(right ?? '').toLowerCase());
    case 'startsWith':
      return String(left ?? '').toLowerCase().startsWith(String(right ?? '').toLowerCase());
    case 'isEmpty':
      return left === undefined || left === null || left === '' ||
        (Array.isArray(left) && left.length === 0);
    case 'isNotEmpty':
      return !(left === undefined || left === null || left === '' ||
        (Array.isArray(left) && left.length === 0));
    case 'in':
      return Array.isArray(right) && (right as unknown[]).includes(left);
    default:
      return false;
  }
}

export function evaluateCondition(ctx: UIContext, cond: AlertCondition | undefined): boolean {
  if (!cond) return false;

  if (cond.all && cond.all.length > 0) {
    return cond.all.every((c) => evaluateCondition(ctx, c));
  }
  if (cond.any && cond.any.length > 0) {
    return cond.any.some((c) => evaluateCondition(ctx, c));
  }
  if (cond.field && cond.operator) {
    return compare(resolveField(ctx, cond.field), cond.operator, cond.value);
  }
  return false;
}

// ──────────────────────────────────────────────────────────────────────
// Default rules – ship-with-the-box behavior. Backend rules will merge
// with these later via setRules().
// ──────────────────────────────────────────────────────────────────────

export const DEFAULT_ALERT_RULES: AlertRule[] = [
  {
    id: 'rule-pending-tasks-high',
    name: 'High pending task backlog',
    message: 'Pending task backlog has grown — triage required.',
    severity: 'HIGH',
    actionType: 'NOTIFY',
    isActive: true,
    condition: { field: 'workflow.pendingTasks', operator: '>', value: 5 },
  },
  {
    id: 'rule-sla-breach-active',
    name: 'SLA breach detected',
    message: 'One or more tasks have breached their SLA.',
    severity: 'CRITICAL',
    actionType: 'ESCALATE',
    targetRole: 'manager',
    isActive: true,
    condition: { field: 'workflow.slaBreached', operator: '>', value: 0 },
  },
  {
    id: 'rule-no-cases',
    name: 'Patient without cases',
    message: 'This patient is enrolled but has no assistance case.',
    severity: 'MEDIUM',
    actionType: 'CREATE_TASK',
    pageTypes: ['Patient360'],
    isActive: true,
    condition: { field: 'entity.cases', operator: '==', value: 0 },
    target: '[data-demo="create-case-btn"]',
  },
  {
    id: 'rule-low-utilization',
    name: 'Utilization below baseline',
    message: 'Program utilization is below 40% — review engagement.',
    severity: 'LOW',
    actionType: 'NOTIFY',
    pageTypes: ['Dashboard', 'Reports'],
    isActive: true,
    condition: {
      all: [
        { field: 'kpi.utiliz', operator: '>', value: 0 },
        { field: 'kpi.utiliz', operator: '<', value: 40 },
      ],
    },
  },
];

// ──────────────────────────────────────────────────────────────────────
// Service – stateful, supports rule replacement + dismissal
// ──────────────────────────────────────────────────────────────────────

class AlertEngineService {
  private rules: AlertRule[] = [...DEFAULT_ALERT_RULES];
  private dismissed = new Set<string>();
  private listeners = new Set<(events: AlertEvent[]) => void>();
  private lastEvents: AlertEvent[] = [];

  /** Replace the rule set (e.g. once backend rules load). */
  setRules(rules: AlertRule[]): void {
    this.rules = rules.slice();
  }

  getRules(): AlertRule[] {
    return this.rules.slice();
  }

  dismiss(eventId: string): void {
    this.dismissed.add(eventId);
    this.lastEvents = this.lastEvents.filter((e) => e.id !== eventId);
    this.notify();
  }

  resetDismissed(): void {
    this.dismissed.clear();
  }

  /**
   * Evaluate all active rules against a context. Generates an AlertEvent
   * for each matching rule. The event id is deterministic
   * (`ruleId|programId|entityId`) so re-evaluation does NOT create
   * duplicates.
   */
  evaluateAlerts(ctx: UIContext): AlertEvent[] {
    const events: AlertEvent[] = [];

    for (const rule of this.rules) {
      if (!rule.isActive) continue;

      // Page-type filter
      if (rule.pageTypes?.length && !rule.pageTypes.includes(ctx.pageType)) {
        continue;
      }

      // Role filter
      if (rule.targetRole && ctx.role) {
        if (!ctx.role.toLowerCase().includes(rule.targetRole.toLowerCase())) {
          continue;
        }
      }

      // Program scope
      if (rule.programId && ctx.programId && rule.programId !== ctx.programId) {
        continue;
      }

      if (!evaluateCondition(ctx, rule.condition)) continue;

      const id = `${rule.id}|${ctx.programId ?? ''}|${ctx.entity?.id ?? ctx.patientId ?? ''}`;
      if (this.dismissed.has(id)) continue;

      events.push({
        id,
        ruleId: rule.id,
        programId: ctx.programId,
        entityId: ctx.entity?.id ?? ctx.patientId,
        entityType: ctx.entity?.type,
        message: rule.message,
        severity: rule.severity,
        actionType: rule.actionType,
        status: 'ACTIVE',
        createdAt: new Date().toISOString(),
        target: rule.target,
      });
    }

    this.lastEvents = events;
    this.notify();
    return events;
  }

  getActive(): AlertEvent[] {
    return this.lastEvents.slice();
  }

  /** Subscribe to alert-list changes (for the header bell). */
  subscribe(fn: (events: AlertEvent[]) => void): () => void {
    this.listeners.add(fn);
    fn(this.lastEvents);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private notify(): void {
    const snapshot = this.lastEvents.slice();
    this.listeners.forEach((l) => l(snapshot));
  }
}

export const alertEngine = new AlertEngineService();

export function severityLabel(s: AlertSeverity): string {
  return s.charAt(0) + s.slice(1).toLowerCase();
}
